/**
 * MultiSlideBuilder Agent - Context Engineering Note
 * 
 * Guidance on context IDs and their usage.
 */

import { PromptSectionBase } from '../../../base/systemPrompt/PromptSectionBase';
import { CONTEXT_ID_PREFIXES } from '../../../base/systemPrompt/shared';
import { MULTI_SLIDE_BUILDER_PROMPT_KEYS } from './variables';

export const MULTI_SLIDE_BUILDER_CONTEXT_PREFIXES: (keyof typeof CONTEXT_ID_PREFIXES)[] = [
    'slide',
    'alphasense',
    'logo',
    'wikimedia',
    'asset',
    'artifact',
    'excelRead',
    'excel',
];

class MultiSlideBuilderContextNoteSection extends PromptSectionBase {
    protected readonly keywordsKey = MULTI_SLIDE_BUILDER_PROMPT_KEYS.contextNote;
    protected readonly fallbackContent = `## Context Engineering

Context items are passed with specific IDs that you must use correctly to link dynamic content.

### Context ID Prefixes

- **slide_xxx**: Slide data from readSlide (shapes, layout, content)
- **alphasense_xxx**: AlphaSense research results (earnings, filings, broker reports)
- **logo_xxx**: Brand logos from Brandfetch (company logos)
- **wikimedia_xxx**: Wikimedia images (stock photos, diagrams)
- **asset_xxx**: Presentation assets (embedded images, media)
- **artifact_xxx**: Generated artifacts (charts, tables for insertion)
- **excelread_xxx**: Excel read results with cell values and formulas
- **excel_xxx**: Excel workbook data and structure

### Using Context IDs

1. **Reference by ID**: When passing context to sub-agents, use the exact ID
   \`\`\`json
   { "context": ["as_001", "logo_acme", "slide_5"], "purpose": "Research, branding, and reference slide" }
   \`\`\`

2. **Include purpose**: Always explain what the context is for

3. **Group related context**: Put related items in the same context array

4. **Don't fabricate IDs**: Only use IDs returned from tools/searches

### Context Flow in MultiSlideBuilder

1. **You receive**: Style guide overview (slide descriptions, layouts, top styles)
2. **You generate**: Research context IDs from search/research tools
3. **You pass to ExecutionAgent**: Context IDs for each slide

**Example flow:**
- Search returns: slide_5, slide_12 (reference slides)
- AlphaSense returns: as_001, as_002 (research results)
- ImageSearch returns: logo_ACME (company logo)
- You define: globalContext with logo_ACME, per-slide context with as_001
- ExecutionAgent uses these IDs to build content`.trim();
}

const contextNoteSection = new MultiSlideBuilderContextNoteSection();

/**
 * Get the context note section.
 * Uses Keywords AI if available, otherwise falls back to code.
 */
export function getContextNoteSection(): string {
    return contextNoteSection.getContent();
}

/**
 * Export constant for backwards compatibility
 */
export const MULTI_SLIDE_BUILDER_CONTEXT_NOTE = contextNoteSection.getFallbackContent();
